import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Spinner, Alert, Row, Col, Table } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import ViewVisitorDetails from './ViewVisitorDetails';

// DTOs
interface VisitorDTO {
  id: number;
  name: string;
  email: string;
  nic: string;
  phone: string;
  visitorCompany: string;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}

interface VehicleDTO {
  id: number;
  vehicleNumber: string;
  parkingSlotId: number | null;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}

interface VisitorRequestDTO {
  id: number;
  locationId: number;
  departmentId: number;
  responsiblePersonEmpNo: string;
  visitorPosition: string;
  visitorReason: string;
  parkingRequired: boolean;
  dateTime: string;
  approvedBy: string | null;
  approvedAt: string | null;
  approvalComment: string | null;
  finalApprovedBy: string | null;
  finalApprovedAt: string | null;
  finalApprovalComment: string | null;
  status: string;
  visitors: VisitorDTO[];
  vehicles: VehicleDTO[];
}

interface GetVisitorResponse {
  status: string;
  statusDescription: string;
  data: VisitorRequestDTO;
  total: number | null;
}

interface ViewVisitorRequestProps {
  show: boolean;
  onHide: () => void;
  visitorRequestId: number;
}

const statusColors: { [key: string]: string } = {
  PENDING: 'badge bg-secondary',
  APPROVED: 'badge bg-success',
  FINAL_APPROVED: 'badge bg-info',
  REJECTED: 'badge bg-danger',
};

const ViewVisitorRequest: React.FC<ViewVisitorRequestProps> = ({ show, onHide, visitorRequestId }) => {
  const [visitorRequest, setVisitorRequest] = useState<VisitorRequestDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showVisitorDetailsModal, setShowVisitorDetailsModal] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState<VisitorDTO | null>(null);

  const fetchVisitorRequest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiDefinitions.getVisitorById(visitorRequestId);
      if (res.data.status === 'SUCCESS') {
        setVisitorRequest(res.data.data);
      } else {
        setError(res.data.statusDescription || 'Failed to fetch visitor request details');
      }
    } catch (err: any) {
      setError('Failed to fetch visitor request details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [visitorRequestId]);

  useEffect(() => {
    if (show) {
      fetchVisitorRequest();
    }
  }, [show, fetchVisitorRequest]);

  const formatDate = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleString();
  };

  const showApprovalBlock = visitorRequest?.approvedBy || visitorRequest?.approvedAt || visitorRequest?.approvalComment;
  const showFinalApprovalBlock = visitorRequest?.finalApprovedBy || visitorRequest?.finalApprovedAt || visitorRequest?.finalApprovalComment;

  const handleViewVisitorClick = (visitor: VisitorDTO) => {
    setSelectedVisitor(visitor);
    setShowVisitorDetailsModal(true);
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Visitor Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span>Loading…</span>
            </div>
          )}

          {error && <Alert variant="danger">{error}</Alert>}

          {!loading && !error && visitorRequest && (
            <>
              {/* Request Details */}
              <Row className="g-4">
                {/* Left Column: Core Details */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Responsible Person Emp No</Form.Label>
                    <Form.Control type="text" value={visitorRequest.responsiblePersonEmpNo || 'N/A'} readOnly />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Visitor Position</Form.Label>
                    <Form.Control type="text" value={visitorRequest.visitorPosition || 'N/A'} readOnly />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Visitor Reason</Form.Label>
                    <Form.Control type="text" value={visitorRequest.visitorReason || 'N/A'} readOnly />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Parking Required</Form.Label>
                    <Form.Control type="text" value={visitorRequest.parkingRequired ? 'Yes' : 'No'} readOnly />
                  </Form.Group>
                </Col>

                {/* Right Column: Additional Details */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date & Time</Form.Label>
                    <Form.Control type="text" value={formatDate(visitorRequest.dateTime) || 'N/A'} readOnly />
                  </Form.Group>
                  {showApprovalBlock && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Approved By</Form.Label>
                        <Form.Control type="text" value={visitorRequest.approvedBy || 'N/A'} readOnly />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Approved At</Form.Label>
                        <Form.Control type="text" value={formatDate(visitorRequest.approvedAt) || 'N/A'} readOnly />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Approval Comment</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={visitorRequest.approvalComment || 'N/A'}
                          readOnly
                        />
                      </Form.Group>
                    </>
                  )}
                  {showFinalApprovalBlock && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Final Approved By</Form.Label>
                        <Form.Control type="text" value={visitorRequest.finalApprovedBy || 'N/A'} readOnly />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Final Approved At</Form.Label>
                        <Form.Control type="text" value={formatDate(visitorRequest.finalApprovedAt) || 'N/A'} readOnly />
                      </Form.Group>
                      <Form.Group className="mb-0">
                        <Form.Label>Final Approval Comment</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={visitorRequest.finalApprovalComment || 'N/A'}
                          readOnly
                        />
                      </Form.Group>
                    </>
                  )}
                </Col>
              </Row>

              {/* Visitors Table */}
              <div className="mt-4">
                <h5 className="mb-3">Visitors</h5>
                {visitorRequest.visitors.length === 0 ? (
                  <Alert variant="info">No visitors found.</Alert>
                ) : (
                  <div className="table-responsive">
                    <Table className="table text-nowrap">
                      <thead className="table-primary">
                        <tr>
                          <th>Name</th>
                          <th>Phone</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitorRequest.visitors.map((visitor) => (
                          <tr key={visitor.id}>
                            <td>{visitor.name || 'N/A'}</td>
                            <td>{visitor.phone || 'N/A'}</td>
                            <td>
                              <div className="hstack gap-2">
                                <Button
                                  variant="icon btn-secondary rounded-circle btn-sm"
                                  title="View Visitor Details"
                                  onClick={() => handleViewVisitorClick(visitor)}
                                >
                                  <i className="feather-eye" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Vehicles Table */}
              <div className="mt-4">
                <h5 className="mb-3">Vehicles</h5>
                {visitorRequest.vehicles.length === 0 ? (
                  <Alert variant="info">No vehicles found.</Alert>
                ) : (
                  <div className="table-responsive">
                    <Table className="table text-nowrap">
                      <thead className="table-primary">
                        <tr>
                          <th>Vehicle Number</th>
                          <th>Parking Slot ID</th>
                          <th>Arrival Time</th>
                          <th>Departure Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitorRequest.vehicles.map((vehicle) => (
                          <tr key={vehicle.id}>
                            <td>{vehicle.vehicleNumber || 'N/A'}</td>
                            <td>{vehicle.parkingSlotId || 'N/A'}</td>
                            <td>{formatDate(vehicle.arrivalTime) || 'N/A'}</td>
                            <td>{formatDate(vehicle.departureTime) || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="d-flex justify-content-end mt-3">
            <Button variant="primary" onClick={onHide}>
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {selectedVisitor && (
        <ViewVisitorDetails
          show={showVisitorDetailsModal}
          onHide={() => setShowVisitorDetailsModal(false)}
          visitor={selectedVisitor}
        />
      )}
    </>
  );
};

export default ViewVisitorRequest;